/**
 * calendarFeedService — the staff-facing side of the subscribable ICS feed
 * (handoff docs/handoffs/trial-lessons-on-a-calendar-2026-08-11.md, step 2).
 *
 * One callable, deliberately narrow. It returns the CALLER's own two
 * subscription links (Lakeville + Headline) and takes no argument that could
 * widen that — a feed URL carries every family name and lesson time, so
 * "fetch someone else's link" is not an operation this surface offers. If Grace
 * needs hers, she opens the same page and it mints hers.
 *
 * There is no direct Firestore read here and there cannot be: `calendarTokens`
 * denies all client access in firestore.rules, so the callable's Admin SDK is
 * the only path to a token. That is the same posture as `leads` — a
 * visibility boundary is a collection wall.
 */

import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

/**
 * The signed-in staff member's own feed links, minted on first call.
 *
 * `rotate: true` disables their current tokens and mints fresh ones — the whole
 * recovery path for a URL that leaked. It cannot be undone: anyone subscribed
 * to the old URL silently stops receiving updates, which is the point.
 *
 * Returns `[{ feedKey, calName, httpsUrl, webcalUrl }]`.
 */
export async function getMyCalendarFeeds({ rotate = false } = {}) {
  const callable = httpsCallable(functions, 'getMyCalendarFeeds');
  const res = await callable(rotate ? { rotate: true } : {});
  return (res.data && res.data.feeds) || [];
}

/**
 * The signed-in FAMILY's single subscription link (customer accounts; a P2b
 * student principal gets a feed scoped to just themselves). Same posture as
 * the staff callable: scope is resolved server-side from the caller's own
 * users doc, and no argument can widen it. `rotate: true` revokes the current
 * link and mints a fresh one.
 *
 * Returns `{ feedKey, calName, httpsUrl, webcalUrl }` or null.
 */
export async function getMyFamilyCalendarFeed({ rotate = false } = {}) {
  const callable = httpsCallable(functions, 'getMyFamilyCalendarFeed');
  const res = await callable(rotate ? { rotate: true } : {});
  return (res.data && res.data.feed) || null;
}
